// Mission + transparency copy for the /about page and the homepage About band.
// Kept identity-free: no founders, team names, photos, or personal narrative.

const SITE = "https://www.homeapproach.com";

export const ABOUT_TITLE = "About Home Approach — Our Mission & How We Make Money";
export const ABOUT_DESCRIPTION =
  "Home Approach is a free homebuying guide that stacks grants, credit boosts, and lender options. Learn our mission, how we fund rewards, and how we stay transparent.";
export const ABOUT_H1 = "About Home Approach";
export const ABOUT_PATH = "/about";
export const ABOUT_URL = `${SITE}${ABOUT_PATH}`;

export type AboutSection = {
  id: string;
  heading: string;
  body: string[];
};

export const ABOUT_SECTIONS: AboutSection[] = [
  {
    id: "mission",
    heading: "Our mission",
    body: [
      "Make homeownership honest, affordable, and achievable — especially for people the traditional system overlooks.",
      "We believe the biggest barrier to buying a home is not ambition; it is missing information. What programs exist, whether they stack, how to qualify, and who to trust should not be secrets locked behind sales calls.",
    ],
  },
  {
    id: "what-we-do",
    heading: "What we actually do",
    body: [
      "We research and match federal, state, county, employer, and nonprofit homeownership programs. We turn that research into a personalized Stack and a step-by-step plan you can follow without paying us a dollar.",
      "Homie, our AI guide, answers questions in plain English, flags the boring-but-important preparation steps, and keeps you moving toward closing day.",
    ],
  },
  {
    id: "how-we-make-money",
    heading: "How we make money",
    body: [
      "Home Approach is free for members. We are not a settlement service provider and do not charge you for matches or introductions.",
      "Home Approach is paid only by employers who offer it as a benefit. No lender, agent, title company, or other provider pays us, not for listings, rankings, introductions, or closings. You are never required to use anyone we suggest, and declining a match costs you nothing. You are free to compare and choose any provider on your own.",
    ],
  },
  {
    id: "what-we-are-not",
    heading: "What we are not",
    body: [
      "We are not a lender, real estate brokerage, credit repair company, or government agency. We do not approve loans, administer grants, or guarantee eligibility.",
      "We are a guide and a matchmaker, not the final decision-maker. A match is an introduction for you to research and evaluate, not an endorsement or recommendation. Always confirm program terms in writing with the administrator and your lender.",
    ],
  },
  {
    id: "homie-note",
    heading: "About Homie",
    body: [
      "Homie is Home Approach's AI guide. Homie is not affiliated with Homie.com or any similarly named brokerage, mortgage company, or app.",
    ],
  },
];

export const ABOUT_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: ABOUT_TITLE,
      description: ABOUT_DESCRIPTION,
      url: ABOUT_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: ABOUT_TITLE, item: ABOUT_URL },
      ],
    },
    {
      "@type": "Organization",
      name: "Home Approach",
      url: SITE,
      description:
        "A free homebuying guide that stacks grants, credit boosts, and lender options while keeping the process transparent and member-first.",
      logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
    },
  ],
};

export const HOMEPAGE_ABOUT_HEADLINE =
  "We built Home Approach because the path to homeownership shouldn't feel rigged.";
export const HOMEPAGE_ABOUT_BODY =
  "Home Approach is a free guide that stacks every grant, credit boost, and lender option you may qualify for — then walks you through the boring-but-important steps with Homie, our AI homebuying coach.";
